/** @typedef {import('./studentProfileStorage').LearningGoal} LearningGoal */
/** @typedef {import('./studentProfileStorage').StudentProfileState} StudentProfileState */

import {
  computeLevelFromCompletionPct,
  dailyMinutesForLevel,
  getSyllabusCompletionFromState,
  LEVEL_META,
} from './studentLevelProgress';
import { addDaysToDayKey, todayKeyInZone } from 'shared/lib/timezone';
import { getStoredUserTimezone } from 'shared/lib/getStoredUserTimezone';

/** @typedef {'topics' | 'minutes' | 'custom'} LearningGoalType */

/**
 * @param {StudentProfileState['analytics']} analytics
 * @param {string} [dateKey] YYYY-MM-DD
 */
export function getStudyMinutesForDay(analytics, dateKey = todayKey()) {
  return Math.round(analytics?.dailyMinutes?.[dateKey] ?? 0);
}

export function getTodayStudyMinutes(analytics) {
  return getStudyMinutesForDay(analytics, todayKey());
}

function todayKey() {
  return todayKeyInZone(getStoredUserTimezone());
}

/** @param {StudentProfileState | { topicProgress?: Record<string, unknown>; profile?: StudentProfileState['profile']; goals?: LearningGoal[] }} state */
function effectiveLevelFromState(state) {
  const { pct } = getSyllabusCompletionFromState(state);
  return computeLevelFromCompletionPct(pct);
}

/**
 * Daily study target (minutes) - derived from level and active goals, not user-edited.
 * @param {StudentProfileState | { profile?: StudentProfileState['profile']; goals?: LearningGoal[] }} state
 */
export function calculateDailyStudyTarget(state) {
  const profile = state?.profile ?? {};
  const goals = state?.goals ?? [];
  const level = effectiveLevelFromState(state ?? { topicProgress: {}, profile, goals });
  const levelBase = dailyMinutesForLevel(level);

  const activeMinutesTargets = goals
    .filter((g) => !g.completed && inferGoalType(g) === 'minutes')
    .map((g) => g.targetMinutesPerDay)
    .filter((n) => typeof n === 'number' && n > 0);

  if (activeMinutesTargets.length > 0) {
    return Math.max(levelBase, Math.max(...activeMinutesTargets));
  }
  return levelBase;
}

/**
 * @param {StudentProfileState | { profile?: StudentProfileState['profile']; goals?: LearningGoal[] }} state
 * @param {boolean} [showEnglish]
 */
export function getDailyTargetHint(state, showEnglish = true) {
  const profile = state?.profile ?? {};
  const goals = state?.goals ?? [];
  const target = calculateDailyStudyTarget(state);
  const level = effectiveLevelFromState(state ?? { topicProgress: {}, profile, goals });
  const levelBase = dailyMinutesForLevel(level);
  const hasMinutesGoal = goals.some(
    (g) => !g.completed && inferGoalType(g) === 'minutes' && (g.targetMinutesPerDay ?? 0) > levelBase
  );

  if (hasMinutesGoal) {
    return showEnglish
      ? `${target} min · from your level and active study goals`
      : `${target} मि. · स्तर और सक्रिय लक्ष्यों से`;
  }

  const meta = LEVEL_META[level] ?? LEVEL_META.beginner;
  const levelLabel = showEnglish ? meta.en : meta.hi;

  return showEnglish
    ? `${target} min · based on ${levelLabel} level (from syllabus progress)`
    : `${target} मि. · ${levelLabel} स्तर (पाठ्यक्रम प्रगति से)`;
}

/**
 * Consecutive days (including today) meeting minutes target, walking backwards.
 * @param {StudentProfileState['analytics']} analytics
 * @param {number} targetMinutes
 */
export function getConsecutiveDaysMeetingMinutes(analytics, targetMinutes) {
  const daily = analytics?.dailyMinutes ?? {};
  let streak = 0;
  let key = todayKey();
  for (let i = 0; i < 366; i += 1) {
    const mins = Math.round(daily[key] ?? 0);
    if (mins >= targetMinutes) {
      streak += 1;
      key = addDaysToDayKey(key, -1);
    } else if (i === 0) {
      key = addDaysToDayKey(key, -1);
    } else {
      break;
    }
  }
  return streak;
}

/**
 * @param {Partial<LearningGoal>} goal
 * @returns {LearningGoalType}
 */
export function inferGoalType(goal) {
  if (goal.goalType === 'topics' || goal.goalType === 'minutes' || goal.goalType === 'custom') {
    return goal.goalType;
  }
  if (goal.targetTopicIds?.length) return 'topics';
  if (goal.targetMinutesPerDay) return 'minutes';
  return 'custom';
}

/**
 * @param {LearningGoal} goal
 * @param {StudentProfileState} state
 * @param {boolean} [showEnglish]
 */
export function computeGoalProgress(goal, state, showEnglish = true) {
  const type = inferGoalType(goal);

  if (type === 'topics') {
    const ids = goal.targetTopicIds ?? [];
    const target = goal.targetCount ?? (ids.length || 1);
    const completed = ids.filter((id) => state.topicProgress[id]?.status === 'completed').length;
    const current = Math.min(completed, target);
    const pct = target > 0 ? Math.round((current / target) * 100) : 0;
    return {
      type,
      current,
      target,
      pct,
      labelEn: `${current}/${target} lessons done`,
      labelHi: `${current}/${target} पाठ पूर्ण`,
      label: showEnglish ? `${current}/${target} lessons done` : `${current}/${target} पाठ पूर्ण`,
      done: current >= target,
    };
  }

  if (type === 'minutes') {
    const targetMinutes = goal.targetMinutesPerDay ?? calculateDailyStudyTarget(state);
    const targetDays = goal.targetCount ?? 7;
    const streak = getConsecutiveDaysMeetingMinutes(state.analytics, targetMinutes);
    const today = getTodayStudyMinutes(state.analytics);
    const current = Math.min(streak, targetDays);
    const pct = targetDays > 0 ? Math.round((current / targetDays) * 100) : 0;
    return {
      type,
      current,
      target: targetDays,
      pct,
      todayMinutes: today,
      targetMinutes,
      labelEn: `${current}/${targetDays} days at ${targetMinutes}+ min · today ${today} min`,
      labelHi: `${current}/${targetDays} दिन ${targetMinutes}+ मि. · आज ${today} मि.`,
      label: showEnglish
        ? `${current}/${targetDays} days at ${targetMinutes}+ min · today ${today} min`
        : `${current}/${targetDays} दिन ${targetMinutes}+ मि. · आज ${today} मि.`,
      done: current >= targetDays,
    };
  }

  return {
    type: 'custom',
    current: goal.completed ? 1 : 0,
    target: 1,
    pct: goal.completed ? 100 : 0,
    labelEn: goal.completed ? 'Marked complete' : 'Mark when done',
    labelHi: goal.completed ? 'पूर्ण चिह्नित' : 'पूर्ण होने पर चिह्नित करें',
    label: showEnglish
      ? goal.completed ? 'Marked complete' : 'Mark when done'
      : goal.completed ? 'पूर्ण चिह्नित' : 'पूर्ण होने पर चिह्नित करें',
    done: goal.completed,
  };
}

/**
 * @param {import('./studentProfileStorage').LearningGoal} goal
 * @param {boolean} showEnglish
 */
export function goalDisplayTitle(goal, showEnglish) {
  return showEnglish ? goal.title : goal.titleHi || goal.title;
}

/** @param {Date} date */
function formatDateKey(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** YYYY-MM-DD for &lt;input type="date" min&gt; and default values. */
export function todayDateInputValue() {
  return formatDateKey(new Date());
}

/** @param {number} [offsetDays] */
export function dateInputValueDaysFromToday(offsetDays = 7) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return formatDateKey(d);
}

/** @param {string | undefined} deadline YYYY-MM-DD */
export function parseGoalDeadline(deadline) {
  if (!deadline || typeof deadline !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(deadline.trim());
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * @param {string | undefined} deadline YYYY-MM-DD
 * @param {boolean} [showEnglish]
 * @returns {{ days: number; expired: boolean; urgent: boolean; label: string; dateLabel: string } | null}
 */
export function getGoalDeadlineCountdown(deadline, showEnglish = true) {
  const end = parseGoalDeadline(deadline);
  if (!end) return null;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const diffDays = Math.round((endDay.getTime() - startOfToday.getTime()) / 86400000);

  const dateLabel = showEnglish
    ? end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : end.toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    return {
      days: diffDays,
      expired: true,
      urgent: true,
      dateLabel,
      label: showEnglish ? `${overdue} day${overdue === 1 ? '' : 's'} overdue` : `${overdue} दिन बीत चुके`,
    };
  }
  if (diffDays === 0) {
    return {
      days: 0,
      expired: false,
      urgent: true,
      dateLabel,
      label: showEnglish ? 'Due today' : 'आज अंतिम दिन',
    };
  }
  if (diffDays === 1) {
    return {
      days: 1,
      expired: false,
      urgent: true,
      dateLabel,
      label: showEnglish ? '1 day left' : '1 दिन बाकी',
    };
  }
  return {
    days: diffDays,
    expired: false,
    urgent: diffDays <= 3,
    dateLabel,
    label: showEnglish ? `${diffDays} days left` : `${diffDays} दिन बाकी`,
  };
}

/**
 * Nearest active goal deadline for home/profile countdown banner.
 * @param {LearningGoal[]} goals
 * @param {boolean} [showEnglish]
 */
export function getNearestActiveGoalDeadline(goals, showEnglish = true) {
  const active = (goals ?? []).filter((g) => !g.completed && g.deadline);
  if (!active.length) return null;

  const sorted = [...active].sort((a, b) => {
    const ta = parseGoalDeadline(a.deadline)?.getTime() ?? Infinity;
    const tb = parseGoalDeadline(b.deadline)?.getTime() ?? Infinity;
    return ta - tb;
  });

  const goal = sorted[0];
  const countdown = getGoalDeadlineCountdown(goal.deadline, showEnglish);
  if (!countdown) return null;
  return { goal, countdown };
}
